/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useMemo } from 'react';
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Loader } from '../components/ui/Loader';
import { chatNavigationQuery } from '@/lib/navigation/projectChat';
import {
  isValidWorkspaceIdFormat,
  resolveWorkspaceAuthority,
  type WorkspaceAuthorityContextValue,
} from '../lib/auth/workspaceAuthority';
import { lookupRuntimeWorkspace } from '../lib/auth/runtimeWorkspaceLookup';
import {
  listAccessibleWorkspaces,
  resolveWorkspaceLanding,
} from '../lib/auth/accessibleWorkspaces';
import { onboardingService } from '@/services/OnboardingService';
import {
  beginContextResolution,
  clearWorkspaceContextHint,
  isCurrentContextResolution,
  readWorkspaceContextHint,
  rememberWorkspaceContextHint,
  resolveContextDestination,
} from '@/lib/auth/contextSwitching';
import { runWorkspaceContextReset } from '@/lib/workspace/workspaceContextReset';
import {
  buildOnboardingHref,
  normalizeReturnSurface,
} from '@/lib/navigation/returnTarget';
import {
  authorityResourceForWorkspacePath,
  resolveLegacyProjectWorkspaceId,
} from '@/services/AuthorityProjectionService';
import {
  useAuthorityProjectionLifecycle,
} from '@/lib/auth/authorityProjectionStore';
import type { AuthorityProjectionRequest } from '@/types/authorityProjection';

/**
 * Route-derived workspace authority context (client UX mirror).
 *
 * The `:workspaceId` route param is the SOLE scope source. The provider may be
 * read for display/caching, but it never substitutes a stored selection, a
 * default, or a legacy role. Server RLS/RPC remains authoritative.
 */
const RESOLVING: WorkspaceAuthorityContextValue = {
  workspaceId: null,
  organizationId: null,
  status: 'resolving',
};

const WorkspaceAuthorityReactContext = createContext<WorkspaceAuthorityContextValue>(RESOLVING);

export function useWorkspaceAuthority(): WorkspaceAuthorityContextValue {
  return useContext(WorkspaceAuthorityReactContext);
}

/**
 * Route boundary: resolves workspace authority from the route param and the
 * authenticated user, then renders one of resolving / invalid / forbidden /
 * ready. No child data query runs until `ready`.
 *
 * - resolving  -> neutral loader (auth still loading)
 * - invalid    -> 404 (missing/malformed/unknown workspace; no metadata)
 * - forbidden  -> unauthorized (exists but no active membership; no metadata)
 * - ready      -> provides context + renders children/Outlet
 */
export const WorkspaceAuthorityBoundary: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { workspaceId } = useParams<{ workspaceId?: string }>();
  const location = useLocation();
  const { user, loading, isDemoMode, authorityVersion } = useAuth();
  const previousScopeRef = React.useRef<{ workspaceId: string | null; userId: string | null }>({
    workspaceId: null,
    userId: null,
  });

  const routeResolution = useMemo<WorkspaceAuthorityContextValue>(() => {
    if (loading) return RESOLVING;
    if (!isDemoMode) {
      if (!workspaceId || !isValidWorkspaceIdFormat(workspaceId)) {
        return { workspaceId: null, organizationId: null, status: 'invalid' };
      }
      if (!user) {
        return { workspaceId: null, organizationId: null, status: 'forbidden' };
      }
      // In connected mode the route supplies only an untrusted scope request.
      // Do not pre-authorize it through legacy user fields or local runtime
      // catalogues: exact external assignments may intentionally have no
      // workspace membership. The server projection resolves existence,
      // canonical organization, and authority for the authenticated actor.
      return { workspaceId, organizationId: null, status: 'ready' };
    }
    return resolveWorkspaceAuthority({
      routeWorkspaceId: workspaceId,
      user,
      lookupWorkspace: (id) => lookupRuntimeWorkspace(id, user),
    });
  }, [workspaceId, user, loading, isDemoMode]);

  const projectionRequest = useMemo<AuthorityProjectionRequest | null>(() => {
    if (isDemoMode || !user?.id || routeResolution.status !== 'ready' || !routeResolution.workspaceId) return null;
    return {
      actorId: user.id,
      // The route does not own organization identity. Null requires the server
      // to derive the canonical organization from the validated workspace.
      organizationId: null,
      workspaceId: routeResolution.workspaceId,
      representedEntity: null,
      resource: authorityResourceForWorkspacePath(location.pathname, routeResolution.workspaceId),
      requestedAction: null,
      actionContext: {},
    };
  }, [isDemoMode, location.pathname, routeResolution.status, routeResolution.workspaceId, user?.id]);

  const {
    state: projectionState,
    outcome: projectionOutcome,
    retry: retryProjection,
  } = useAuthorityProjectionLifecycle({
    request: projectionRequest,
    enabled: !isDemoMode,
    authorityVersion,
  });

  const value = useMemo<WorkspaceAuthorityContextValue>(() => {
    if (isDemoMode || routeResolution.status !== 'ready') return routeResolution;
    if (projectionOutcome !== 'confirmed' || projectionState.status !== 'confirmed') return RESOLVING;
    return {
      workspaceId: projectionState.projection.scope.workspaceId,
      organizationId: projectionState.projection.scope.organizationId,
      status: 'ready',
    };
  }, [isDemoMode, projectionOutcome, projectionState, routeResolution]);

  React.useEffect(() => {
    const nextWorkspaceId = value.status === 'ready' ? value.workspaceId : null;
    runWorkspaceContextReset({
      previousWorkspaceId: previousScopeRef.current.workspaceId,
      nextWorkspaceId,
      previousUserId: previousScopeRef.current.userId,
      nextUserId: user?.id ?? null,
    });

    if (user?.id && value.status === 'ready' && value.workspaceId && value.organizationId) {
      rememberWorkspaceContextHint(
        user.id,
        { id: value.workspaceId, organizationId: value.organizationId },
        'route',
      );
    }

    if (user?.id && (value.status === 'forbidden' || value.status === 'invalid')) {
      clearWorkspaceContextHint(user.id);
    }

    previousScopeRef.current = {
      workspaceId: nextWorkspaceId,
      userId: user?.id ?? null,
    };
  }, [value, user?.id]);

  if (routeResolution.status === 'resolving') {
    return <div className="min-h-screen bg-app" aria-busy="true"><Loader /></div>;
  }
  if (routeResolution.status === 'invalid') {
    return <Navigate to="/404" replace />;
  }
  if (routeResolution.status === 'forbidden') {
    return <Navigate to="/unauthorized" replace />;
  }
  if (!isDemoMode && projectionRequest) {
    if (projectionOutcome === 'loading') {
      return <div className="min-h-screen bg-app" aria-busy="true"><Loader /></div>;
    }
    if (projectionOutcome === 'denied') {
      return <Navigate to="/unauthorized" replace />;
    }
    if (projectionOutcome === 'unavailable') {
      return (
        <main className="min-h-screen bg-app p-6" role="alert" aria-live="assertive">
          <div className="mx-auto max-w-lg rounded-lg border border-border bg-surface p-6">
            <h1 className="text-lg font-semibold text-text-primary">Authority check unavailable</h1>
            <p className="mt-2 text-sm text-text-secondary">
              Your access was not denied, but it cannot be verified right now. No protected content has loaded.
            </p>
            <button
              type="button"
              className="mt-4 rounded-md border border-border px-3 py-2 text-sm font-medium text-text-primary"
              onClick={retryProjection}
            >
              Retry authority check
            </button>
          </div>
        </main>
      );
    }
  }
  return (
    <WorkspaceAuthorityReactContext.Provider value={value}>
      {children ?? <Outlet />}
    </WorkspaceAuthorityReactContext.Provider>
  );
};

/**
 * Legacy flat-surface entry point (e.g. `/tasks`, `/projects`, `/assets`).
 *
 * Resolves the user's VALIDATED accessible workspaces and routes
 * deterministically — it NEVER auto-selects the first of many:
 *   - zero    -> onboarding / no-access (never an empty selector)
 *   - one     -> the scoped canonical route for that single workspace (replace)
 *   - many    -> explicit selection at /workspaces/select
 *
 * `subPath` is the workspace-scoped suffix to land on for the single-workspace
 * case (e.g. 'tasks', 'projects', 'assets', 'companies', '' for the dashboard).
 */
export const LegacyWorkspaceSurfaceRedirect: React.FC<{
  subPath?: string;
  /** Connected membership-backed entry must resolve through the server selector. */
  connectedServerResolution?: boolean;
  /** Exact project id used for an RLS-filtered project -> workspace route hint. */
  connectedProjectId?: string;
}> = ({
  subPath = '',
  connectedServerResolution = false,
  connectedProjectId,
}) => {
  const { user, loading, isDemoMode, authorityVersion } = useAuth();
  const location = useLocation();
  const [resolvedTarget, setResolvedTarget] = React.useState<string | null>(null);
  const [resolvingTarget, setResolvingTarget] = React.useState(true);
  const [projectRetryVersion, setProjectRetryVersion] = React.useState(0);
  const [projectResolution, setProjectResolution] = React.useState<
    | { status: 'idle' | 'loading' }
    | { status: 'resolved'; workspaceId: string }
    | { status: 'denied' }
    | { status: 'unavailable' }
  >({ status: 'idle' });
  const chatQuery = subPath === 'chat' ? chatNavigationQuery(location.search) : '';
  const connected = isDemoMode === false;
  const usesConnectedSelector = connected && connectedServerResolution;
  const usesConnectedProjectResolution = connected && Boolean(connectedProjectId);

  const landing = useMemo(() => {
    if (loading || usesConnectedSelector || usesConnectedProjectResolution) return null;
    return resolveWorkspaceLanding(listAccessibleWorkspaces(user, (id) => lookupRuntimeWorkspace(id, user)));
  }, [loading, user, usesConnectedProjectResolution, usesConnectedSelector]);

  const settingsTab = React.useMemo(() => {
    if (subPath !== 'settings') return null;
    const candidate = new URLSearchParams(location.search).get('tab');
    return candidate && [
      'account',
      'notifications',
      'appearance',
      'integrations',
      'organization',
      'workspace',
      'theme',
    ].includes(candidate) ? candidate : null;
  }, [location.search, subPath]);

  const connectedSelectorTarget = useMemo(() => {
    if (!usesConnectedSelector || !user?.id) return '/login';
    const params = new URLSearchParams();
    const hint = readWorkspaceContextHint(user.id);
    if (hint?.workspaceId) params.set('requestedWorkspaceId', hint.workspaceId);
    const surface = subPath === 'settings/invitations'
      ? 'invitation-settings'
      : subPath === 'invitations'
        ? 'invitations'
        : normalizeReturnSurface(subPath);
    if (surface) params.set('returnTo', surface);
    return `/workspaces/select${params.size ? `?${params.toString()}` : ''}`;
  }, [subPath, user?.id, usesConnectedSelector]);

  React.useEffect(() => {
    if (!usesConnectedProjectResolution || loading || !user?.id || !connectedProjectId) {
      setProjectResolution({ status: 'idle' });
      return;
    }

    let cancelled = false;
    setProjectResolution({ status: 'loading' });
    void resolveLegacyProjectWorkspaceId(connectedProjectId)
      .then((workspaceId) => {
        if (cancelled) return;
        setProjectResolution(workspaceId
          ? { status: 'resolved', workspaceId }
          : { status: 'denied' });
      })
      .catch(() => {
        if (!cancelled) setProjectResolution({ status: 'unavailable' });
      });
    return () => {
      cancelled = true;
    };
  }, [
    authorityVersion,
    connectedProjectId,
    loading,
    projectRetryVersion,
    user?.id,
    usesConnectedProjectResolution,
  ]);

  React.useEffect(() => {
    let cancelled = false;

    const resolveTarget = async () => {
      if (usesConnectedSelector || usesConnectedProjectResolution) return;
      if (loading || landing === null) {
        return;
      }

      const userId = user?.id;
      if (!userId) {
        if (!cancelled) {
          setResolvedTarget('/login');
          setResolvingTarget(false);
        }
        return;
      }

      const resolutionToken = beginContextResolution(userId);
      const isCurrent = () => !cancelled && isCurrentContextResolution(userId, resolutionToken);
      const savedHint = readWorkspaceContextHint(userId);
      const destination = resolveContextDestination({
        landing,
        requestedWorkspaceId: savedHint?.workspaceId,
        source: 'saved',
      });

      if (destination.kind === 'denied') {
        clearWorkspaceContextHint(userId);
        if (isCurrent()) {
          setResolvedTarget('/unauthorized');
          setResolvingTarget(false);
        }
        return;
      }

      if (destination.kind === 'none') {
        clearWorkspaceContextHint(userId);
        if (isCurrent()) {
          setResolvedTarget('/unauthorized');
          setResolvingTarget(false);
        }
        return;
      }

      if (destination.kind === 'scoped') {
        try {
          const state = await onboardingService.initialize(destination.workspaceId);
          if (isCurrent()) {
            const scopedWorkspace = landing.kind === 'one'
              ? landing.workspace
              : landing.kind === 'many'
                ? landing.workspaces.find((candidate) => candidate.id === destination.workspaceId)
                : null;
            if (scopedWorkspace) {
              rememberWorkspaceContextHint(userId, scopedWorkspace, 'saved');
            }
            if (!state.isComplete) {
              setResolvedTarget(buildOnboardingHref(destination.workspaceId, subPath));
            } else {
              const suffix = subPath ? `/${subPath}` : '';
              const settingsQuery = settingsTab ? `?tab=${encodeURIComponent(settingsTab)}` : '';
              setResolvedTarget(`/workspaces/${destination.workspaceId}${suffix}${chatQuery || settingsQuery}`);
            }
            setResolvingTarget(false);
          }
          return;
        } catch {
          if (isCurrent()) {
            setResolvedTarget(buildOnboardingHref(null, subPath));
            setResolvingTarget(false);
          }
          return;
        }
      }

      try {
        const contexts = await onboardingService.getContexts();
        for (const context of contexts) {
          const state = await onboardingService.initialize(context.workspaceId);
          if (!state.isComplete) {
            if (!cancelled) {
              setResolvedTarget(buildOnboardingHref(null, subPath));
              setResolvingTarget(false);
            }
            return;
          }
        }
      } catch {
        if (isCurrent()) {
          setResolvedTarget(buildOnboardingHref(null, subPath));
          setResolvingTarget(false);
        }
        return;
      }

      if (isCurrent()) {
        const params = new URLSearchParams();
        if (destination.requestedWorkspaceId) {
          params.set('requestedWorkspaceId', destination.requestedWorkspaceId);
        }
        const surface = normalizeReturnSurface(subPath);
        if (surface) params.set('returnTo', surface);
        if (settingsTab) params.set('settingsTab', settingsTab);
        const query = params.toString() ? `?${params.toString()}` : '';
        setResolvedTarget(`/workspaces/select${query}`);
        setResolvingTarget(false);
      }
    };

    setResolvingTarget(true);
    void resolveTarget();

    return () => {
      cancelled = true;
    };
  }, [
    landing,
    loading,
    settingsTab,
    chatQuery,
    subPath,
    user?.id,
    usesConnectedProjectResolution,
    usesConnectedSelector,
  ]);

  if (usesConnectedSelector) {
    if (loading) {
      return <div className="min-h-screen bg-app" aria-busy="true"><Loader /></div>;
    }
    return <Navigate to={user ? connectedSelectorTarget : '/login'} replace />;
  }

  if (usesConnectedProjectResolution) {
    if (!loading && !user) return <Navigate to="/login" replace />;
    if (loading || projectResolution.status === 'idle' || projectResolution.status === 'loading') {
      return <div className="min-h-screen bg-app" aria-busy="true"><Loader /></div>;
    }
    if (projectResolution.status === 'denied') {
      return <Navigate to="/unauthorized" replace />;
    }
    if (projectResolution.status === 'unavailable') {
      return (
        <main className="min-h-screen bg-app p-6" role="alert" aria-live="assertive">
          <div className="mx-auto max-w-lg rounded-lg border border-border bg-surface p-6">
            <h1 className="text-lg font-semibold text-text-primary">Route check unavailable</h1>
            <p className="mt-2 text-sm text-text-secondary">
              The project workspace cannot be verified right now. No project content has loaded.
            </p>
            <button
              type="button"
              className="mt-4 rounded-md border border-border px-3 py-2 text-sm font-medium text-text-primary"
              onClick={() => setProjectRetryVersion((version) => version + 1)}
            >
              Retry route check
            </button>
          </div>
        </main>
      );
    }
    if (projectResolution.status !== 'resolved') {
      return <Navigate to="/unauthorized" replace />;
    }
    return (
      <Navigate
        to={`/workspaces/${encodeURIComponent(projectResolution.workspaceId)}/${subPath}`}
        replace
      />
    );
  }

  if (loading || landing === null || resolvingTarget) {
    return <div className="min-h-screen bg-app" aria-busy="true"><Loader /></div>;
  }
  return <Navigate to={resolvedTarget ?? '/onboarding'} replace />;
};
